(function(){
  // guestxm.js — sends a copy of the contact form to a proxy endpoint which forwards to GuestXM
  // Endpoint: https://stately-arithmetic-fe5a3f.netlify.app/.netlify/functions/guestxm-push
  // You can override endpoint by adding data-guestxm-endpoint attribute to the form element.

  /**
   * @typedef {Object.<string, string>} FormFields
   */

  /**
   * @param {HTMLFormElement} form
   * @returns {FormFields}
   */
  function serializeForm(form) {
    /** @type {FormFields} */
    var obj = {};
    var fd = new FormData(form);
    fd.forEach(function(value, key){
      // normalize Shopify contact fields: contact[name], contact[email], contact[body]
      obj[key] = typeof value === 'string' ? value : '';
    });
    return obj;
  }

  /**
   * @typedef {Object} GuestXMPayload
   * @property {string} template_id
   * @property {string} location_name
   * @property {Object} payload
   * @property {string} payload.restaurant
   * @property {string} payload.ID
   * @property {string} payload.visit_date
   * @property {string} payload.name
   * @property {string} payload.email
   * @property {string} payload.comment_type
   * @property {number|null} payload.rating
   * @property {number} payload.contact_person
   * @property {string} payload.message
   * @property {string} [payload.phone]
   * @property {string} [payload.request_type]
   * @property {string} [payload.event_date]
   * @property {string} [payload.organization_name]
   * @property {string} [payload.mailing_address]
   * @property {string} [payload.expected_attendance]
   * @property {string} [payload.organization_size]
   * @property {string} [payload.organization_type]
   * @property {string} [payload.special_requests]
   * @property {string} [payload.hp]
   * @property {string} [payload.form_loaded_at]
   */

  /**
   * @param {FormFields} fields
   * @returns {GuestXMPayload}
   */
  /**
   * Generate a unique ID for this submission
   * Format: timestamp-random (e.g., "1698432567890-a1b2c3d4")
   */
  function generateUniqueId() {
    var timestamp = Date.now();
    var random = Math.random().toString(36).substring(2, 10); // 8 char random string
    return timestamp + '-' + random;
  }

  /**
   * Copy fields[key] onto target[outKey], but only when the form actually carries a
   * non-empty value. Fields a given form doesn't have — or that are disabled because a
   * conditional block is hidden (FormData skips disabled controls, so serializeForm never
   * sees them) — are omitted from the payload entirely rather than sent as empty strings.
   *
   * @param {Object} target
   * @param {string} outKey
   * @param {FormFields} fields
   * @param {string} key
   */
  function addIfPresent(target, outKey, fields, key) {
    var value = fields[key];
    if (typeof value === 'string' && value.trim() !== '') {
      target[outKey] = value;
    }
  }

  function buildPayload(fields) {
    // fields is the plain object collected from FormData
    // Map the existing form fields to the GuestXM payload shown in your example.
    var locationName = fields['comment[location_id]'] || '';
    var commentType = fields['comment[comment_type_id]'] || fields['comment[comment_type]'] || '';
    var contactPerson = (fields['comment[contact_person]'] || '').toLowerCase() === 'yes' ? 1 : 0;

    // visit_date: map from date_of_visit field (format: YYYY-MM-DD to MMDDYYYY)
    var visitDate = fields['comment[date_of_visit]'] || '';
    if (visitDate) {
      // Convert YYYY-MM-DD to MMDDYYYY
      var parts = visitDate.split('-');
      if (parts.length === 3) {
        visitDate = parts[1] + parts[2] + parts[0]; // MM + DD + YYYY
      }
    }

    // Rating: use experience_rating field
    var rating = fields['comment[experience_rating]'] ? Number(fields['comment[experience_rating]']) : null;

    var payload = {
      restaurant: 'Lazy Dog',
      ID: generateUniqueId(), // Generate unique ID for each submission
      visit_date: visitDate,
      name: fields['contact[name]'] || fields['comment[full_name]'] || '',
      email: fields['contact[email]'] || fields['comment[email_address]'] || '',
      comment_type: commentType,
      rating: rating,
      contact_person: contactPerson,
      message: fields['contact[body]'] || fields['comment[comment]'] || ''
    };

    // Additional whitelisted fields; shape agreed with the Netlify side, which renders only
    // the keys present in the payload. `phone` also fixes the long-standing empty phone line
    // in the existing routed emails — comment[telephone] was never forwarded.
    // The rest back the Local Marketing form (sections/ld-local-marketing-form.liquid);
    // the fundraiser-only ones are absent unless that conditional block was visible.
    addIfPresent(payload, 'phone', fields, 'comment[telephone]');
    addIfPresent(payload, 'request_type', fields, 'comment[request_type]');
    addIfPresent(payload, 'event_date', fields, 'comment[event_date]');
    addIfPresent(payload, 'organization_name', fields, 'comment[organization_name]');
    addIfPresent(payload, 'mailing_address', fields, 'comment[mailing_address]');
    addIfPresent(payload, 'expected_attendance', fields, 'comment[expected_attendance]');
    addIfPresent(payload, 'organization_size', fields, 'comment[organization_size]');
    addIfPresent(payload, 'organization_type', fields, 'comment[organization_type]');
    addIfPresent(payload, 'special_requests', fields, 'comment[special_requests]');

    // Spam signals for the Netlify-side filter. `hp` is the honeypot: addIfPresent omits it
    // unless it is non-empty, and a non-empty value IS the signal. `form_loaded_at` is the
    // page-load timestamp and always rides. No detection happens here.
    addIfPresent(payload, 'hp', fields, 'website');
    addIfPresent(payload, 'form_loaded_at', fields, 'form_loaded_at');

    return {
      template_id: '', // <-- set on server or configure here if needed
      // location_id: either send numeric id here or send location_name so server can map it
      location_name: locationName,
      payload: payload
    };
  }

  /**
   * @param {GuestXMPayload} jsonPayload
   * @param {string} endpoint
   * @returns {Promise<any>}
   */
  function sendToProxy(jsonPayload, endpoint) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jsonPayload),
      mode: 'cors',
      // keepalive: this is a fire-and-forget copy fired on submit without preventDefault.
      // The contact form redirects to /pages/thank-you once its own POST resolves, which unloads
      // the page; without keepalive that navigation kills this request in flight (Netlify 499).
      keepalive: true
    }).then(function(res){
      if (!res.ok) throw new Error('Proxy response ' + res.status);
      return res.json().catch(function(){ return { ok: true }; });
    });
  }

  /**
   * Attach the capture listener to one form. Idempotent: this script is included by more
   * than one section (lazy-dog-contact, ld-contact-form, ld-local-marketing-form), so it can
   * execute twice on a page that carries two of them — without the marker that would bind
   * twice and double-POST.
   *
   * @param {Element|null} formElement
   */
  function bindForm(formElement) {
    // Explicitly check if the element is a form
    if (!formElement || !(formElement instanceof HTMLFormElement)) {
      return;
    }
    if (formElement.dataset.guestxmBound === '1') {
      return;
    }
    formElement.dataset.guestxmBound = '1';

    var form = formElement;
    form.addEventListener('submit', function(e){
      try {
        var fields = serializeForm(form);
        var endpoint = form.dataset.guestxmEndpoint || 'https://stately-arithmetic-fe5a3f.netlify.app/.netlify/functions/guestxm-push';
        var payload = buildPayload(fields);

        // non-blocking: send an async copy to our proxy, do not prevent the default form submission
        sendToProxy(payload, endpoint).catch(function(err){
          // eslint-disable-next-line no-console
          console.warn('GuestXM proxy error:', err);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('GuestXM client error', err);
      }

      // allow form to submit normally (Shopify contact form)
    }, { passive: true });
  }

  function init() {
    // Contact form — unchanged resolution order (id first, then the legacy class fallback).
    bindForm(document.getElementById('new_comment') || document.querySelector('form.new_comment'));
    // Local Marketing form. Its own handler preventDefaults the native navigation and shows an
    // inline confirmation instead of redirecting, but does not stopPropagation, so this
    // bubble-phase listener still sees the submit event.
    bindForm(document.getElementById('local-marketing-form'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Already parsed (e.g. a second, cached execution of this script): bind immediately
    // instead of waiting for a DOMContentLoaded that has already fired.
    init();
  }
})();
